"use client"

import { useState } from "react"
import * as XLSX from "xlsx"
import { useExplorer } from "@/hooks/use-explorer"
import { useTranslation } from "@/hooks/use-translation"
import type { TranslationKey } from "@/lib/translations"
import { useMappingConfig } from "@/hooks/use-mapping-config"
import { useTargetMapping } from "@/hooks/use-target-mapping"
import { useAtsSource } from "@/contexts/ats-source-context"
import {
  buildOnboardingWorkbook,
  getGeneratableSheets,
  rowMatchesConditions,
  translateRow,
  type SheetData,
} from "@/lib/onboarding-export"
import type { AtsExplorerEntityMapping, AtsExplorerFilter } from "@/types/ats-explorer-api"

const ONBOARDING_TARGET = "OnboardingImport"

export interface OnboardingExportProgress {
  sheetName: string
  status: "pending" | "pulling" | "done" | "skipped" | "error"
  rowCount?: number
  message?: string
}

export interface OnboardingExportOptions {
  maxRecords: number
  includeRelatedEntities: boolean
  searchText?: string | null
  filters?: AtsExplorerFilter[] | null
}

/**
 * Generates an Onboarding Import .xlsx from ATS data via the two-layer mapping:
 * 1. ATS -> FBO: each sheet's source mapping type pulls and maps records server-side
 *    (mapped-pull, existing ATS->FBO field mapping configs)
 * 2. FBO -> sheet columns: the tenant's FboTargetMappingConfiguration translates
 *    FBO rows to exact sheet column headers client-side
 */
export function useOnboardingExport() {
  const { t, n } = useTranslation()
  const { mappedPull } = useExplorer()
  const { loadConfig } = useMappingConfig()
  const { loadTargetMapping, loadTargetSpec } = useTargetMapping()
  const { selectedSource } = useAtsSource()
  const [progress, setProgress] = useState<OnboardingExportProgress[]>([])
  const [isExporting, setIsExporting] = useState(false)

  const exportOnboardingFile = async (atsSystem: string, options: OnboardingExportOptions): Promise<void> => {
    setIsExporting(true)

    try {
      const [targetMapping, targetSpec] = await Promise.all([
        loadTargetMapping(ONBOARDING_TARGET),
        loadTargetSpec(ONBOARDING_TARGET),
      ])

      const generatableSheets = getGeneratableSheets(targetMapping.sheets ?? [], targetSpec)

      if (generatableSheets.length === 0) {
        throw new Error(t("pull.onboarding.errors.noSheets"))
      }

      setProgress(generatableSheets.map((s) => ({ sheetName: s.sheetName, status: "pending" })))

      const sheetData: SheetData[] = []

      // An onboarding export is generated for ONE source — the selected source (atsSystem
      // is its system). Every sheet pulls from it and contributes the shared FBO columns
      // plus this source's ATS column overlay; other sources' ATS columns belong to their
      // own export and are left out here (coexistence is by design — no per-sheet
      // single-source constraint).
      const exportSourceCode = selectedSource?.code ?? null

      for (const sheet of generatableSheets) {
        updateSheetProgress(setProgress, sheet.sheetName, { status: "pulling" })

        try {
          // Layer 1: ATS -> FBO via the saved mapping config for this sheet's source type
          const mappingConfig = await loadConfig(sheet.sourceMappingName!, exportSourceCode)
          const entities = mappingConfig.entities ?? []

          if (entities.length === 0 || entities.every((e) => (e.fieldMappings?.length ?? 0) === 0)) {
            updateSheetProgress(setProgress, sheet.sheetName, {
              status: "skipped",
              message: t("pull.onboarding.errors.noFieldMappings", { name: sheet.sourceMappingName }),
            })
            continue
          }

          const explorerEntities: AtsExplorerEntityMapping[] = entities.map((entity) => ({
            entityType: entity.entityType,
            name: entity.name,
            referenceId: entity.referenceId ?? null,
            fieldMappings: (entity.fieldMappings ?? []).map((fm) => ({
              sourceField: fm.sourceField,
              destinationField: fm.destinationField,
              dataTypeConverter: fm.dataTypeConverter ?? null,
              valueMappings: fm.valueMappings ?? null,
              defaultValueMapping: fm.defaultValueMapping,
            })),
          }))

          // FBO columns are a shared set; ATS columns are layered in per source. Use the
          // shared FBO columns plus this sheet's source's ATS columns — an ATS column
          // overrides an FBO column that targets the same sheet column. ATS columns ride
          // through the mapping engine via a synthetic field mapping under a unique flat
          // key that the FBO -> column translation then reads like any other FBO field.
          const atsColumnsForSource = sheet.columnMappings.filter(
            (c) =>
              c.sourceType === "Ats" &&
              c.sourceAtsField &&
              (!c.sourceAtsSource || c.sourceAtsSource.toLowerCase() === (exportSourceCode ?? "").toLowerCase())
          )
          const overriddenTargets = new Set(atsColumnsForSource.map((c) => c.targetColumn))

          const effectiveColumnMappings = [
            ...sheet.columnMappings.filter((c) => c.sourceType !== "Ats" && !overriddenTargets.has(c.targetColumn)),
            ...atsColumnsForSource.map((mapping, i) => {
              const syntheticDestination = `__ats.c${i}`
              const targetEntity =
                explorerEntities.find((e) => e.name === mapping.sourceAtsEntityName) ?? explorerEntities[0]

              targetEntity.fieldMappings.push({
                sourceField: mapping.sourceAtsField!,
                destinationField: syntheticDestination,
              })

              return { ...mapping, sourceFboField: syntheticDestination }
            }),
          ]

          const result = await mappedPull(atsSystem, {
            entityType: explorerEntities[0].entityType,
            searchText: options.searchText ?? null,
            filters: options.filters ?? null,
            maxRecords: options.maxRecords,
            entities: explorerEntities,
            includeRelatedEntities: options.includeRelatedEntities && explorerEntities.length > 1,
          })

          // Row conditions: only eligible FBO rows are written to the sheet
          const eligibleRows = result.rows.filter((fboRow) => rowMatchesConditions(fboRow, sheet.rowConditions))
          const excludedByConditions = result.rows.length - eligibleRows.length

          // Layer 2: FBO -> sheet columns
          const rows = eligibleRows.map((fboRow) => translateRow(fboRow, effectiveColumnMappings))
          sheetData.push({ sheetName: sheet.sheetName, rows })

          const notes = [
            result.skippedCount > 0 ? t(plural(result.skippedCount, "skipped"), { count: n(result.skippedCount) }) : null,
            excludedByConditions > 0 ? t(plural(excludedByConditions, "excluded"), { count: n(excludedByConditions) }) : null,
          ].filter(Boolean)

          updateSheetProgress(setProgress, sheet.sheetName, {
            status: "done",
            rowCount: rows.length,
            message: notes.length > 0 ? notes.join("; ") : undefined,
          })
        } catch (err) {
          updateSheetProgress(setProgress, sheet.sheetName, {
            status: "error",
            message: err instanceof Error ? err.message : t("pull.onboarding.errors.pullFailed"),
          })
          throw err
        }
      }

      if (sheetData.every((s) => s.rows.length === 0)) {
        throw new Error(t("pull.onboarding.errors.noRecords"))
      }

      const workbook = buildOnboardingWorkbook(sheetData, targetSpec)
      XLSX.writeFile(workbook, `onboarding-import-${atsSystem.toLowerCase()}.xlsx`)
    } finally {
      setIsExporting(false)
    }
  }

  return { exportOnboardingFile, progress, isExporting }
}

/** Picks the singular or plural key for one of the per-sheet progress notes */
function plural(count: number, note: "skipped" | "excluded"): TranslationKey {
  const suffix = count === 1 ? "One" : "Other"
  return `pull.onboarding.notes.${note}${suffix}` as TranslationKey
}

function updateSheetProgress(
  setProgress: React.Dispatch<React.SetStateAction<OnboardingExportProgress[]>>,
  sheetName: string,
  update: Partial<OnboardingExportProgress>
) {
  setProgress((current) => current.map((p) => (p.sheetName === sheetName ? { ...p, ...update } : p)))
}
